import { useRef, useCallback } from 'react';

import {
  useBraintreeExecuteMutation,
  BraintreeExecuteRequest,
  BraintreeExecuteResponse,
} from '@/api/payments/braintreeExecute';
import { BraintreeCreateData } from '@/api/payments/braintreeCreate';
import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { useClient } from '@/services/client/useClient';
import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';

const buildExecutePayload = (
  paymentSelectState: PaymentSelectState,
  client: string,
  paymentData: ApplePayJS.ApplePayPaymentAuthorizedEvent,
  nonce: string,
  createData: BraintreeCreateData,
): BraintreeExecuteRequest => ({
  client,
  nonce,
  transactionId: createData.transactionId,
  account_identifier: createData.account_identifier,
  amount: paymentSelectState.amount,
  currency: paymentSelectState.currency,
  firstName: paymentData.payment.billingContact?.givenName ?? '',
  lastName: paymentData.payment.billingContact?.familyName ?? '',
  email: paymentData.payment.shippingContact?.emailAddress ?? '',
  paymentType: 'APPLEPAY',
});

export function useBraintreeExecute() {
  const paymentSelectState = useAppSelector(state => state.paymentSelect);

  const [braintreeExecuteTrigger] = useBraintreeExecuteMutation();

  const { getClientId } = useClient();

  const onSuccess = useRef<((data: BraintreeExecuteResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);

  // Trigger payment-service execute
  const triggerExecuteApi = useCallback(async (
    paymentData: ApplePayJS.ApplePayPaymentAuthorizedEvent,
    nonce: string,
    createData: BraintreeCreateData,
    callbacks?: {
      onSuccess?: (data: BraintreeExecuteResponse) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    onSuccess.current = callbacks?.onSuccess;
    onError.current = callbacks?.onError;

    if (!nonce) {
      logger.error('Braintree nonce not set');
      if (onError.current) {
        onError.current(new Error('Braintree nonce not set'));
      }
      return null;
    }

    let result;
    try {
      const payload = buildExecutePayload(
        paymentSelectState,
        getClientId(),
        paymentData,
        nonce,
        createData,
      );
      result = await braintreeExecuteTrigger(payload);
    } catch (err) {
      logger.error('Error executing Braintree transaction');
      logger.error(err);
      if (onError.current) {
        onError.current(err);
      }
    }

    // Handle payment-service execute errors
    if (!!result?.error || result?.data?.message !== 'success') {
      logger.error('Error executing Braintree transaction');
      logger.error(result?.error);
      if (onError.current) {
        onError.current(result?.error);
      }
      return null;
    }

    // Handle payment-service execute complete
    if (onSuccess.current) {
      onSuccess.current(result?.data);
    }

    return result?.data;
  }, [braintreeExecuteTrigger, getClientId, paymentSelectState]);

  return {
    triggerExecuteApi
  };
}
